import { isNumber } from 'lodash'

import type { TrackingEvent } from '@marketplace-web/observability/event-tracker-data'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { ContentSource } from 'constants/tracking/content-sources'
import { ListItemContentType } from 'constants/tracking/content-types'
import { Screen } from 'constants/tracking/screens'

type ClickClosetPromotionEventArgs = {
  ownerId: number
  contentSource: ContentSource
  target: string
}

type ClickClosetPromotionEventExtra = {
  owner_id: number
  content_source: ContentSource
  target: string
}

export const clickClosetPromotionEvent = (args: ClickClosetPromotionEventArgs) => {
  const { contentSource, target, ownerId } = args

  const extra: ClickClosetPromotionEventExtra = {
    owner_id: ownerId,
    content_source: contentSource,
    target,
  }

  return {
    event: 'user.click_closet_promotion',
    extra,
  }
}

type ClickReduceItemPriceEventArgs = {
  screen: string
}

type ClickReduceItemPriceEventExtra = {
  screen: string
}

export const clickReduceItemPriceEvent = (args: ClickReduceItemPriceEventArgs): TrackingEvent => {
  const { screen } = args

  const extra: ClickReduceItemPriceEventExtra = { screen }

  return {
    event: 'user.click_reduce_item_price',
    extra,
  }
}

type ClickConfirmReduceItemPriceEventArgs = {
  screen: string
}

type ClickConfirmReduceItemPriceEventExtra = {
  screen: string
}

export const clickConfirmReduceItemPriceEvent = (
  args: ClickConfirmReduceItemPriceEventArgs,
): TrackingEvent => {
  const { screen } = args

  const extra: ClickConfirmReduceItemPriceEventExtra = { screen }

  return {
    event: 'user.click_confirm_reduce_item_price',
    extra,
  }
}

type ClickCancelReduceItemPriceEventArgs = {
  screen: string
}

type ClickCancelReduceItemPriceEventExtra = {
  screen: string
}

export const clickCancelReduceItemPriceEvent = (
  args: ClickCancelReduceItemPriceEventArgs,
): TrackingEvent => {
  const { screen } = args

  const extra: ClickCancelReduceItemPriceEventExtra = { screen }

  return {
    event: 'user.click_cancel_reduce_item_price',
    extra,
  }
}

type ReducePriceSuccessfullyEventArgs = {
  screen?: string
}

type ReducePriceSuccessfullyEventExtra = {
  screen?: string
}

export const reducePriceSuccessfullyEvent = (
  args: ReducePriceSuccessfullyEventArgs,
): TrackingEvent => {
  const { screen } = args

  const extra: ReducePriceSuccessfullyEventExtra = { screen }

  return {
    event: 'user.reduce_price_successfully',
    extra,
  }
}

type ClickListAnItemEventArgs = {
  screen: string
}

type ClickListAnItemEventExtra = {
  screen: string
}

export const clickListAnItemEvent = (args: ClickListAnItemEventArgs): TrackingEvent => {
  const { screen } = args

  const extra: ClickListAnItemEventExtra = { screen }

  return {
    event: 'user.click_list_an_item',
    extra,
  }
}

type ViewSoldBumpCelebrationEventArgs = {
  screen: string
}

type ViewSoldBumpCelebrationEventExtra = {
  screen: string
}

export const viewSoldBumpCelebrationEvent = (
  args: ViewSoldBumpCelebrationEventArgs,
): TrackingEvent => {
  const { screen } = args

  const extra: ViewSoldBumpCelebrationEventExtra = { screen }

  return {
    event: 'user.view_sold_bump_celebration',
    extra,
  }
}

type ClickBumpMoreItemsEventArgs = {
  screen: string
}

type ClickBumpMoreItemsEventExtra = {
  screen: string
}

export const clickBumpMoreItemsEvent = (args: ClickBumpMoreItemsEventArgs): TrackingEvent => {
  const { screen } = args

  const extra: ClickBumpMoreItemsEventExtra = { screen }

  return {
    event: 'user.click_bump_more_items',
    extra,
  }
}

type ClickEventArgs = {
  target: ClickableElement
  screen?: string | null
  targetDetails?: string | null
  path?: string | null
  env?: string | null
}

type ClickEventExtra = {
  target: ClickableElement
  screen?: string
  target_details?: string
  path?: string | null
  env?: string | null
}

export const clickEvent = (args: ClickEventArgs): TrackingEvent => {
  const { target, screen, targetDetails, env, path } = args
  const extra: ClickEventExtra = { target }

  if (screen) extra.screen = screen

  if (targetDetails) extra.target_details = targetDetails

  if (env) extra.env = env

  if (path) extra.path = path

  return {
    event: 'user.click',
    extra,
  }
}

type FavouriteItemEventArgs = {
  itemId: number
  isFollowEvent: boolean
  contentSource?: ContentSource | null
  searchCorrelationId?: string
  searchSessionId?: string
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  homepageSessionId?: string
  itemOwnerId?: string | number | null
}

type FavouriteItemEventExtra = {
  item_id: number
  content_source?: ContentSource
  search_correlation_id?: string
  search_session_id?: string
  global_search_session_id?: string | null
  global_catalog_browse_session_id?: string | null
  homepage_session_id?: string
  item_owner_id?: string | number
}

export const favouriteItemEvent = ({
  itemId,
  isFollowEvent,
  contentSource,
  searchCorrelationId,
  searchSessionId,
  globalSearchSessionId,
  globalCatalogBrowseSessionId,
  homepageSessionId,
  itemOwnerId,
}: FavouriteItemEventArgs): TrackingEvent => {
  const eventName = isFollowEvent ? 'user.favorite_item' : 'user.unfavorite_item'
  const extra: FavouriteItemEventExtra = { item_id: itemId }

  if (contentSource) extra.content_source = contentSource
  if (searchCorrelationId) extra.search_correlation_id = searchCorrelationId
  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId
  if (homepageSessionId) extra.homepage_session_id = homepageSessionId
  if (itemOwnerId) extra.item_owner_id = itemOwnerId

  return {
    event: eventName,
    extra,
  }
}

type ImpressionEventArgs = {
  id: number | string
  contentType: ListItemContentType
  contentSource: ContentSource
  position: number
  itemDistance?: number | null
  itemOwnerId?: string | number | null
  searchScore?: string
  searchSignals?: Array<string> | null
  searchCorrelationId?: string
  searchSessionId?: string | null
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  homepageSessionId?: string
  uploadSessionId?: string
  screen?: Screen
  metadata?: Record<string, Array<string>>
}

type ImpressionEventExtra = {
  content_type: ListItemContentType
  content_source: ContentSource
  id: number | string
  position: number
  item_owner_id?: string | number
  search_score?: string
  search_signals?: Array<string>
  search_correlation_id?: string
  search_session_id?: string
  global_search_session_id?: string | null
  global_catalog_browse_session_id?: string | null
  buyer_item_distance_km?: number
  homepage_session_id?: string
  upload_session_id?: string
  screen?: Screen
  metadata?: Record<string, Array<string>>
}

export const impressionEvent = (args: ImpressionEventArgs): TrackingEvent => {
  const {
    id,
    contentType,
    contentSource,
    itemDistance,
    itemOwnerId,
    position,
    searchScore,
    searchSignals,
    searchCorrelationId,
    searchSessionId,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
    homepageSessionId,
    uploadSessionId,
    screen,
    metadata,
  } = args

  const extra: ImpressionEventExtra = {
    id,
    position,
    content_type: contentType,
    content_source: contentSource,
  }

  if (itemOwnerId) extra.item_owner_id = itemOwnerId
  if (searchScore) extra.search_score = JSON.stringify(searchScore)
  if (searchSignals) extra.search_signals = searchSignals
  if (searchCorrelationId) extra.search_correlation_id = searchCorrelationId
  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId
  if (isNumber(itemDistance)) extra.buyer_item_distance_km = itemDistance
  if (homepageSessionId) extra.homepage_session_id = homepageSessionId
  if (uploadSessionId) extra.upload_session_id = uploadSessionId
  if (screen) extra.screen = screen
  if (metadata) extra.metadata = metadata

  return {
    event: 'list.show_item',
    extra,
  }
}

type ClickListItemEventArgs = {
  id: number | string
  contentType: ListItemContentType
  contentSource: ContentSource
  position: number
  searchCorrelationId?: string
  searchSessionId?: string | null
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  searchScore?: string
  searchSignals?: Array<string> | null
  itemDistance?: number | null
  homepageSessionId?: string
  screen?: Screen
  metadata?: Record<string, Array<string>>
}

type ClickListEventExtra = {
  content_type: ListItemContentType
  content_source: ContentSource
  id: number | string
  position: number
  search_correlation_id?: string
  search_session_id?: string
  global_search_session_id?: string | null
  global_catalog_browse_session_id?: string | null
  search_score?: string
  search_signals?: Array<string>
  buyer_item_distance_km?: number
  homepage_session_id?: string
  screen?: Screen
  metadata?: Record<string, Array<string>>
}

export const clickListItemEvent = (args: ClickListItemEventArgs): TrackingEvent => {
  const {
    id,
    contentType,
    contentSource,
    position,
    searchCorrelationId,
    searchSessionId,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
    searchScore,
    searchSignals,
    itemDistance,
    homepageSessionId,
    screen,
    metadata,
  } = args

  const extra: ClickListEventExtra = {
    id,
    position,
    content_type: contentType,
    content_source: contentSource,
  }

  if (searchCorrelationId) extra.search_correlation_id = searchCorrelationId
  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId
  if (searchScore) extra.search_score = searchScore
  if (searchSignals) extra.search_signals = searchSignals
  if (isNumber(itemDistance)) extra.buyer_item_distance_km = itemDistance
  if (homepageSessionId) extra.homepage_session_id = homepageSessionId
  if (screen) extra.screen = screen
  if (metadata) extra.metadata = metadata

  return {
    event: 'user.click_list_item',
    extra,
  }
}

// Source: https://github.com/vinted/dwh-schema-registry/blob/master/avro/events/user/view.avdl#L25
type ViewEventTarget =
  | 'see_whole_closet_cta'
  | 'cp_reminder'
  | 'bump_precheckout_review_order_action'

type ViewEventArgs = {
  target: ViewEventTarget
  targetDetails?: string
  screen?: string
}

type ViewEventExtra = {
  target: ViewEventTarget
  target_details?: string
  screen?: string
}

export const viewEvent = (args: ViewEventArgs): TrackingEvent => {
  const { target, targetDetails, screen } = args

  const extra: ViewEventExtra = {
    target,
  }

  if (targetDetails) extra.target_details = targetDetails
  if (screen) extra.screen = screen

  return {
    event: 'user.view',
    extra,
  }
}

type ViewScreenEventArgs = {
  screen: string
}

export const viewScreenEvent = (args: ViewScreenEventArgs): TrackingEvent => {
  const { screen } = args

  const extra = {
    screen,
  }

  return {
    event: 'user.view_screen',
    extra,
  }
}

type UserInputEventArgs = {
  screen?: Screen
  target: string
  value?: string | null
  state: 'focus' | 'unfocus'
  tempUuid?: string
  isDynamic?: boolean
}

export const userInputEvent = (args: UserInputEventArgs): TrackingEvent => {
  const { state, target, value, screen, tempUuid, isDynamic } = args
  const extra = {
    screen,
    target,
    value,
    state,
    upload_session_id: tempUuid,
    is_dynamic: isDynamic || false,
  }

  return {
    event: 'user.input',
    extra,
  }
}

type ItemUploadClickEventArgs = {
  screen?: Screen
  target: ClickableElement
  targetDetails?: string | null
  uploadSessionId: string | null
}

type ItemUploadClickEventExtra = {
  screen?: Screen
  target: ClickableElement
  target_details?: string | null
  upload_session_id: string | null
}

export const itemUploadClickEvent = (args: ItemUploadClickEventArgs): TrackingEvent => {
  const { screen, target, targetDetails, uploadSessionId } = args

  const extra: ItemUploadClickEventExtra = {
    target,
    upload_session_id: uploadSessionId,
  }

  if (screen) extra.screen = screen
  if (targetDetails) extra.target_details = targetDetails

  return {
    event: 'upload_form.user.click',
    extra,
  }
}
